import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useClientServices } from './useData';
import { formatDate } from '../../config/appConfig';
import { formatCurrency } from '../../utils/formatCurrency';
import { useTheme } from '../../contexts/ThemeContext';
import { moduleConfig, getTableActions, getColumns, getToolbarIcon } from './config';
import type { ClientService } from './useData';
import { renderRenewalStatusBadge } from '../service-renewal-reminders/config';
import DataTable, { Column } from '../../components/DataTable';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';
import { useNotification } from '../../contexts/NotificationContext';
import { useSorting } from '../../hooks/useSorting';
import { XIcon, ChevronDownIcon, ColoredBadge } from '../../components/ui';
import { cn } from '../../lib/utils';
import { ReportExportButtons } from '../../components/reports/ReportExportButtons';
import { exportReportToExcel, exportReportToPDF } from '../../utils/reportExport';
import ServiceRenewalRemindersPanel from './ServiceRenewalRemindersPanel';

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** Effective end date: serviceEndDate or serviceTerminationDate. */
function getEndDate(s: ClientService): string | undefined {
  return s.serviceEndDate || s.serviceTerminationDate;
}

/** True if end date is within 15 days from today or already passed. */
function isEndDateWithin15Days(endDateStr: string | undefined | null): boolean {
  if (!endDateStr) return false;
  const end = new Date(endDateStr);
  if (isNaN(end.getTime())) return false;
  const now = new Date();
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const endDay = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate());
  const oneDay = 24 * 60 * 60 * 1000;
  const daysUntil = (endDay - today) / oneDay;
  return daysUntil <= 15;
}

const RENEWAL_STATUS_EXPORT_LABELS: Record<string, string> = {
  pending: 'Pending',
  renewed: 'Renewed',
  declined: 'Declined',
  no_response: 'No response',
  will_proceed: 'Will proceed',
};

const ClientServicesYearlyView: React.FC = () => {
  const { colors, theme } = useTheme();
  const { services, isLoading, isError, mutate } = useClientServices();
  const { showSuccess, showError } = useNotification();
  const navigate = useNavigate();
  const isMobile = false;
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  /** Selected month 0-11, or null for all. Default: current month. */
  const [selectedMonth, setSelectedMonth] = useState<number | null>(() => new Date().getMonth());
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [viewDropdownOpen, setViewDropdownOpen] = useState(false);
  const [viewDropdownPosition, setViewDropdownPosition] = useState<{ top: number; right: number } | null>(null);
  const viewDropdownRef = useRef<HTMLDivElement>(null);
  const viewButtonRef = useRef<HTMLButtonElement>(null);
  const viewMenuRef = useRef<HTMLDivElement>(null);
  const [remindersPanelOpen, setRemindersPanelOpen] = useState(false);
  const [selectedServiceForReminders, setSelectedServiceForReminders] = useState<ClientService | null>(null);

  const openRenewalRemindersPanel = useCallback((service: ClientService) => {
    setSelectedServiceForReminders(service);
    setRemindersPanelOpen(true);
  }, []);

  const closeRenewalRemindersPanel = useCallback(() => {
    setRemindersPanelOpen(false);
    setSelectedServiceForReminders(null);
  }, []);

  // Reuse same panel as list: reminder icon opens panel in same view (slide-over), no navigation
  const listContext = useMemo(
    () => ({ onOpenRenewalRemindersPanel: openRenewalRemindersPanel }),
    [openRenewalRemindersPanel]
  );

  useEffect(() => {
    if (!viewDropdownOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        viewDropdownRef.current &&
        viewMenuRef.current &&
        !viewDropdownRef.current.contains(event.target as Node) &&
        !viewMenuRef.current.contains(event.target as Node)
      ) {
        setViewDropdownOpen(false);
        setViewDropdownPosition(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [viewDropdownOpen]);

  // Only services with an end date
  const withEndDate = useMemo(() => {
    return services.filter((s) => getEndDate(s));
  }, [services]);

  // Year options: only years that have at least one service end date
  const yearOptions = useMemo<number[]>(() => {
    const years = new Set<number>();
    withEndDate.forEach((s) => {
      const d = getEndDate(s);
      if (d) years.add(new Date(d).getFullYear());
    });
    const list = Array.from(years).sort((a, b) => a - b);
    return list.length > 0 ? list : [currentYear];
  }, [withEndDate, currentYear]);

  // Keep selected year in the list when options are based on data
  useEffect(() => {
    if (yearOptions.length > 0 && !yearOptions.includes(selectedYear)) {
      setSelectedYear(yearOptions[yearOptions.length - 1]);
    }
  }, [yearOptions, selectedYear]);

  // Ending in selected year
  const endingInYear = useMemo(() => {
    return withEndDate.filter((s) => {
      const d = getEndDate(s);
      return d && new Date(d).getFullYear() === selectedYear;
    });
  }, [withEndDate, selectedYear]);

  // By month for selected year: count, sum of invoice, cost, profit
  const byMonth = useMemo(() => {
    const data = Array.from({ length: 12 }, () => ({ count: 0, invoice: 0, cost: 0, profit: 0 }));
    endingInYear.forEach((s) => {
      const d = getEndDate(s);
      if (d) {
        const i = new Date(d).getMonth();
        data[i].count += 1;
        data[i].invoice += s.serviceInvoice || 0;
        data[i].cost += s.serviceCost || 0;
        data[i].profit += s.serviceProfit || 0;
      }
    });
    return data;
  }, [endingInYear]);

  // When a month is selected, filter to that month only; otherwise show all for the year
  const displayedServices = useMemo(() => {
    if (selectedMonth === null) return endingInYear;
    return endingInYear.filter((s) => {
      const d = getEndDate(s);
      return d && new Date(d).getMonth() === selectedMonth;
    });
  }, [endingInYear, selectedMonth]);

  const totals = useMemo(() => {
    const cost = displayedServices.reduce((s, x) => s + (x.serviceCost || 0), 0);
    const inv = displayedServices.reduce((s, x) => s + (x.serviceInvoice || 0), 0);
    const profit = displayedServices.reduce((s, x) => s + (x.serviceProfit || 0), 0);
    return { cost, invoice: inv, profit, count: displayedServices.length };
  }, [displayedServices]);

  // Sorting support
  const { sortConfig, handleSort } = useSorting<ClientService>({
    key: 'serviceEndDate',
    direction: 'asc',
  });

  // Apply sorting to displayed services
  const tableRows = useMemo(() => {
    if (!sortConfig) {
      // Default: sort by end date ascending
      return [...displayedServices].sort(
        (a, b) => new Date(getEndDate(a) || 0).getTime() - new Date(getEndDate(b) || 0).getTime()
      );
    }

    return [...displayedServices].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      // Handle special cases
      if (sortConfig.key === 'serviceEndDate') {
        aValue = getEndDate(a) ? new Date(getEndDate(a)!).getTime() : 0;
        bValue = getEndDate(b) ? new Date(getEndDate(b)!).getTime() : 0;
      } else if (sortConfig.key === 'client.clientName') {
        aValue = a.client?.clientName || '';
        bValue = b.client?.clientName || '';
      } else {
        // Get value from row
        aValue = (a as any)[sortConfig.key];
        bValue = (b as any)[sortConfig.key];
      }

      // Handle null/undefined
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Compare
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aStr = String(aValue);
      const bStr = String(bValue);
      const comparison = aStr.localeCompare(bStr);
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [displayedServices, sortConfig]);

  // Handle delete
  const handleDelete = async (id: number) => {
    try {
      await moduleConfig.delete.handler(id);
      mutate();
      setDeleteConfirm(null);
      showSuccess('Service deleted successfully');
    } catch (error: any) {
      console.error('Failed to delete:', error);
      showError(moduleConfig.delete.errorMessage);
    }
  };

  // Get base columns from config (with listContext so reminder column has clickable button)
  const baseColumns = useMemo(
    () => getColumns(formatDate, undefined, colors, listContext),
    [colors, listContext]
  );
  
  // Define columns for DataTable - adapted from base columns with footer support
  const columns: Column<ClientService>[] = useMemo(() => {
    // Find and adapt the columns we need from baseColumns
    const serviceDescrColumn = baseColumns.find(col => col.key === 'serviceDescr');
    const serviceEndDateColumn = baseColumns.find(col => col.key === 'serviceEndDate');
    const serviceTypeColumn = baseColumns.find(col => col.key === 'serviceType');
    const serviceCategoryColumn = baseColumns.find(col => col.key === 'serviceCategory');
    const renewalRemindersColumn = baseColumns.find(col => col.key === 'renewalReminders');
    const serviceInvoiceColumn = baseColumns.find(col => col.key === 'serviceInvoice');
    const serviceCostColumn = baseColumns.find(col => col.key === 'serviceCost');
    const serviceProfitColumn = baseColumns.find(col => col.key === 'serviceProfit');

    return [
      {
        ...serviceDescrColumn!,
        key: 'serviceDescr',
        header: 'Description',
        render: (value: ClientService['serviceDescr'], row: ClientService) => {
          const text = value ? (value.length > 50 ? value.substring(0, 50) + '...' : value) : null;
          const tags = row.serviceTags || [];
          const rawClientName = row.client?.clientName || (row.clientId ? `Client #${row.clientId}` : '');
          const clientName = rawClientName.length > 30 ? rawClientName.substring(0, 30) + '...' : rawClientName;
          const hasContent = tags.length > 0 || text;
          if (!hasContent) return <span className={cn('text-sm', colors.textMuted)}>-</span>;
          return (
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-1.5">
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {tags.map((tag: { name?: string; color?: string }, index: number) => (
                      <ColoredBadge key={index} label={tag.name ?? ''} color={tag.color || '#94a3b8'} />
                    ))}
                  </div>
                )}
                {tags.length > 0 && text && <span className={cn('text-xs', colors.textMuted)}>·</span>}
                {text && (
                  <Link
                    to={moduleConfig.module.route.detail(row.clientServicetId)}
                    className="text-indigo-600 hover:text-indigo-800 hover:underline"
                  >
                    {text}
                  </Link>
                )}
              </div>
              {rawClientName && (
                <div className={cn('mt-0.5 text-xs truncate', colors.textMuted)} title={rawClientName}>
                  {clientName}
                </div>
              )}
            </div>
          );
        },
        footerType: 'custom',
        footerRender: () => 'Total',
        footerLabel: '',
      },
      {
        ...serviceEndDateColumn!,
        key: 'serviceEndDate',
        header: 'End Date',
        render: (_value: ClientService['serviceEndDate'], row: ClientService) => {
          const endDateStr = getEndDate(row);
          const formatted = formatDate(endDateStr);
          if (!formatted) return '-';
          const showWarning = isEndDateWithin15Days(endDateStr);
          return (
            <span className="inline-flex items-center gap-1.5">
              {showWarning && (
                <svg className="w-4 h-4 shrink-0 text-amber-500 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-label="End date within 15 days">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              )}
              <span>{formatted}</span>
            </span>
          );
        },
        footerType: 'custom',
        footerRender: () => null,
        footerLabel: '',
      },
      {
        ...serviceTypeColumn!,
        key: 'serviceType',
        header: 'Service Type',
        footerType: 'custom',
        footerRender: () => null,
        footerLabel: '',
      },
      {
        ...serviceCategoryColumn!,
        key: 'serviceCategory',
        footerType: 'custom',
        footerRender: () => null,
        footerLabel: '',
      },
      ...(renewalRemindersColumn
        ? [{
            ...renewalRemindersColumn,
            key: 'renewalReminders',
            header: 'Reminder',
            render: (_value: ClientService['renewalReminders'], row: ClientService) => {
              const reminders = row.renewalReminders ?? [];
              const count = reminders.length;
              const last = reminders[0];
              const lastDate = last?.sentAt ? formatDate(last.sentAt) : null;
              const lastStatusValue = last?.renewalResponseStatus ?? null;
              const responseDate = last?.renewalResponseAt ? formatDate(last.renewalResponseAt) : null;
              const latestReminder = row.renewalReminders?.[0];
              const reminderNote = (latestReminder?.renewalResponseNote ?? latestReminder?.note ?? '').trim();
              const noteIcon = reminderNote ? (
                <span
                  className={cn('inline-flex items-center justify-center shrink-0 cursor-help', colors.textMuted)}
                  title={reminderNote}
                  aria-label={`Reminder note: ${reminderNote}`}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </span>
              ) : null;

              const onOpenPanel = listContext?.onOpenRenewalRemindersPanel;
              if (count === 0) {
                if (onOpenPanel) {
                  return (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onOpenPanel(row);
                      }}
                      className="text-indigo-600 hover:text-indigo-800 hover:underline text-left"
                    >
                      0 reminders
                    </button>
                  );
                }
                return '-';
              }

              const responseLine = lastStatusValue ? (
                <div className="flex flex-wrap items-center gap-1.5">
                  {renderRenewalStatusBadge(lastStatusValue)}
                  {noteIcon}
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className={cn('text-xs', colors.textMuted)}>—</span>
                  {noteIcon}
                </div>
              );

              const responseLineBottom = responseDate ? (
                <div className={cn('text-xs mt-0.5', colors.textMuted)}>
                  Responded {responseDate}
                </div>
              ) : null;
              const sentLine = lastDate ? (
                <div className={cn('text-xs mt-0.5', colors.textMuted)}>
                  Sent {lastDate} · {count} reminder{count !== 1 ? 's' : ''}
                </div>
              ) : null;

              if (onOpenPanel) {
                return (
                  <div className="text-left">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onOpenPanel(row);
                      }}
                      className="text-left w-full hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded px-1 -mx-1 py-0.5"
                    >
                      {responseLine}
                      {responseLineBottom}
                      {sentLine}
                    </button>
                  </div>
                );
              }

              return (
                <div className="text-left">
                  {responseLine}
                  {responseLineBottom}
                  {sentLine}
                </div>
              );
            },
            footerType: 'custom' as const,
            footerRender: () => null,
            footerLabel: '',
          }]
        : []),
      {
        ...serviceInvoiceColumn!,
        key: 'serviceInvoice',
        align: 'right' as const,
        footerType: 'custom',
        footerRender: (_value: unknown, allData: ClientService[]) => {
          const sum = allData.reduce((acc: number, row: ClientService) => acc + (row.serviceInvoice || 0), 0);
          return `$${sum.toFixed(2)}`;
        },
        footerLabel: '',
      },
      {
        ...serviceCostColumn!,
        key: 'serviceCost',
        align: 'right' as const,
        footerType: 'custom',
        footerRender: (_value: unknown, allData: ClientService[]) => {
          const sum = allData.reduce((acc: number, row: ClientService) => acc + (row.serviceCost || 0), 0);
          return `$${sum.toFixed(2)}`;
        },
        footerLabel: '',
      },
      {
        ...serviceProfitColumn!,
        key: 'serviceProfit',
        align: 'right' as const,
        footerType: 'custom',
        footerRender: (_value: unknown, allData: ClientService[]) => {
          const sum = allData.reduce((acc: number, row: ClientService) => acc + (row.serviceProfit || 0), 0);
          return `$${sum.toFixed(2)}`;
        },
        footerLabel: '',
      },
    ];
  }, [baseColumns, colors]);

  const reportTitle = `Services Ending in ${selectedYear}`;
  const fileName = `client-services-yearly-${selectedYear}`;
  const onExportExcel = useCallback(async () => {
    const headers = ['Description', 'End Date', 'Service Type', 'Service Category', 'Reminder (Client response)', 'Invoice', 'Cost', 'Profit'];
    const rows: (string | number)[][] = tableRows.map((row) => {
      const tagNames = (row.serviceTags?.map((t) => t.name) ?? []).join(', ');
      const descr = row.serviceDescr ?? '';
      const description = tagNames ? (descr ? `${descr} (${tagNames})` : tagNames) : descr;
      const reminders = row.renewalReminders ?? [];
      const reminderCount = reminders.length;
      const last = reminders[0];
      const lastStatus = last?.renewalResponseStatus;
      const statusLabel = lastStatus ? (RENEWAL_STATUS_EXPORT_LABELS[lastStatus] ?? lastStatus) : null;
      const responseDate = last?.renewalResponseAt ? formatDate(last.renewalResponseAt) : null;
      let reminderCell = '—';
      if (reminderCount > 0) {
        const parts = [`${reminderCount} reminder${reminderCount !== 1 ? 's' : ''}`];
        if (statusLabel) parts.push(statusLabel);
        if (responseDate) parts.push(responseDate);
        reminderCell = parts.join(' · ');
      }
      return [
        description,
        formatDate(getEndDate(row) ?? ''),
        row.serviceTypeDescr?.pst_descr ?? '',
        row.serviceCategoryDescr?.psc_descr ?? '',
        reminderCell,
        row.serviceInvoice ?? '',
        row.serviceCost ?? '',
        row.serviceProfit ?? '',
      ];
    });
    if (rows.length > 0) {
      rows.push(['Total', '', '', '', '', totals.invoice, totals.cost, totals.profit]);
    }
    await exportReportToExcel({ reportTitle, fileName, table: { headers, rows } });
  }, [tableRows, reportTitle, fileName, totals]);
  const onExportPDF = useCallback(async () => {
    const headers = ['Description', 'End Date', 'Service Type', 'Service Category', 'Reminder (Client response)', 'Invoice', 'Cost', 'Profit'];
    const rows: (string | number)[][] = tableRows.map((row) => {
      const tagNames = (row.serviceTags?.map((t) => t.name) ?? []).join(', ');
      const descr = row.serviceDescr ?? '';
      const description = tagNames ? (descr ? `${descr} (${tagNames})` : tagNames) : descr;
      const reminders = row.renewalReminders ?? [];
      const reminderCount = reminders.length;
      const last = reminders[0];
      const lastStatus = last?.renewalResponseStatus;
      const statusLabel = lastStatus ? (RENEWAL_STATUS_EXPORT_LABELS[lastStatus] ?? lastStatus) : null;
      const responseDate = last?.renewalResponseAt ? formatDate(last.renewalResponseAt) : null;
      let reminderCell = '—';
      if (reminderCount > 0) {
        const parts = [`${reminderCount} reminder${reminderCount !== 1 ? 's' : ''}`];
        if (statusLabel) parts.push(statusLabel);
        if (responseDate) parts.push(responseDate);
        reminderCell = parts.join(' · ');
      }
      return [
        description,
        formatDate(getEndDate(row) ?? ''),
        row.serviceTypeDescr?.pst_descr ?? '',
        row.serviceCategoryDescr?.psc_descr ?? '',
        reminderCell,
        row.serviceInvoice ?? '',
        row.serviceCost ?? '',
        row.serviceProfit ?? '',
      ];
    });
    if (rows.length > 0) {
      rows.push(['Total', '', '', '', '', totals.invoice, totals.cost, totals.profit]);
    }
    await exportReportToPDF({ reportTitle, fileName, table: { headers, rows } });
  }, [tableRows, reportTitle, fileName, totals]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className={colors.textMuted}>Loading services...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className={colors.textMuted}>Failed to load services.</div>
      </div>
    );
  }

  const monthLabel = selectedMonth != null ? MONTHS[selectedMonth] : null;

  const viewToggleViews = [
    { key: 'list', label: 'List', icon: 'list', href: '/client-services' },
    { key: 'renewal', label: 'Renewal Reminders', icon: 'reminder', href: '/service_renewal_reminders' },
    { key: 'yearly', label: 'Services', icon: 'chart', href: '/client-services-yearly' },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Year on the left; View options + Excel/PDF on the right */}
      <div className="mb-2 flex-shrink-0 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className={cn('text-xs font-medium mr-1', colors.textMuted)}>Year</span>
          <div className={cn('inline-flex rounded-lg p-1 gap-1.5 border', colors.border, colors.surface)} role="group" aria-label="Select year">
            {yearOptions.map((y: number) => (
              <button
                key={y}
                type="button"
                onClick={() => { setSelectedYear(y); setSelectedMonth(null); }}
                className={cn(
                  'min-w-[2.75rem] px-2.5 py-1.5 text-sm font-medium rounded-md transition-colors',
                  selectedYear === y
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : cn(colors.text, colors.surfaceHover, 'hover:bg-gray-100 dark:hover:bg-gray-700')
                )}
                aria-pressed={selectedYear === y}
              >
                {y}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/client-services/new"
            title="New Service"
            className={cn(
              'inline-flex items-center justify-center h-9 w-9 rounded-lg border font-medium text-sm transition-colors',
              'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700 hover:border-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
            )}
          >
            {getToolbarIcon('add')}
          </Link>
          <div className="relative flex-shrink-0 inline-block" ref={viewDropdownRef}>
            <button
              ref={viewButtonRef}
              type="button"
              onClick={() => {
                if (!viewDropdownOpen && viewButtonRef.current) {
                  const rect = viewButtonRef.current.getBoundingClientRect();
                  setViewDropdownPosition({
                    top: rect.bottom + 4,
                    right: window.innerWidth - rect.right,
                  });
                  setViewDropdownOpen(true);
                } else {
                  setViewDropdownOpen(false);
                  setViewDropdownPosition(null);
                }
              }}
              className={cn(
                'inline-flex items-center justify-center h-9 min-w-[2.25rem] px-2.5 rounded-lg border transition-colors',
                viewDropdownOpen
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : cn(colors.surface, colors.text, colors.border, colors.interactiveHover)
              )}
              title="View Options"
            >
              <svg className="w-5 h-5 text-inherit shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 13a1 1 0 011-1h4a1 1 0 011 1v6a1 1 0 01-1 1h-4a1 1 0 01-1-1v-6z" />
              </svg>
              <ChevronDownIcon
                className={cn('w-4 h-4 ml-1 transition-transform text-inherit', viewDropdownOpen ? 'rotate-180' : '')}
              />
            </button>
            {viewDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-[90]"
                  aria-hidden
                  onClick={() => {
                    setViewDropdownOpen(false);
                    setViewDropdownPosition(null);
                  }}
                />
                <div
                  ref={viewMenuRef}
                  className={cn('fixed z-[100] min-w-[180px] rounded-lg shadow-xl border overflow-hidden', colors.surface, colors.border)}
                  style={
                    viewDropdownPosition
                      ? { top: `${viewDropdownPosition.top}px`, right: `${viewDropdownPosition.right}px` }
                      : undefined
                  }
                >
                  {viewToggleViews.map((view) => (
                    <Link
                      key={view.key}
                      to={view.href}
                      onClick={() => {
                        setViewDropdownOpen(false);
                        setViewDropdownPosition(null);
                      }}
                      className={cn('w-full px-3 py-2 text-sm text-left transition-colors flex items-center gap-2', colors.text)}
                    >
                      <span className="text-inherit">{getToolbarIcon(view.icon)}</span>
                      <span>{view.label}</span>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>
          <ReportExportButtons onExportExcel={onExportExcel} onExportPDF={onExportPDF} disabled={tableRows.length === 0} />
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col">
        <div className={`flex-shrink-0 ${isMobile ? 'px-2' : 'px-0'} mb-2`}>
          <div className={`space-y-2 sm:space-y-3`}>
          {/* Month Grid */}
          <div className={`${colors.surface} ${colors.border} border rounded-xl ${isMobile ? 'px-3 py-3' : 'px-4 py-3'} shadow-md bg-gradient-to-br ${theme === 'dark' ? 'from-gray-800 to-gray-800' : 'from-white to-gray-50'}`}>
            <div className="flex items-center justify-between mb-2.5">
              <h3 className={`text-sm font-semibold ${colors.text} flex items-center gap-2`}>
                <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>Monthly Breakdown ({selectedYear})</span>
              </h3>
              {monthLabel && (
                <button
                  type="button"
                  onClick={() => setSelectedMonth(null)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${colors.textMuted} hover:${colors.text} ${colors.surfaceHover} transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                  title="Clear month filter"
                >
                  <XIcon className="w-3.5 h-3.5" />
                  <span>Clear filter</span>
                </button>
              )}
            </div>
            {monthLabel && (
              <div className={`mb-3 px-3 py-1.5 rounded-lg ${theme === 'dark' ? 'bg-indigo-900/20' : 'bg-indigo-50'} border ${theme === 'dark' ? 'border-indigo-800' : 'border-indigo-200'}`}>
                <p className={`text-xs ${colors.text}`}>
                  <span className="font-medium">Filtered:</span> Showing services ending in <span className="font-semibold text-indigo-600 dark:text-indigo-400">{monthLabel} {selectedYear}</span>
                </p>
              </div>
            )}
            <div className={`grid ${isMobile ? 'grid-cols-3 sm:grid-cols-6' : 'grid-cols-6 sm:grid-cols-12'} gap-2`}>
              {MONTHS.map((m, i) => {
                const isSelected = selectedMonth === i;
                const hasData = byMonth[i].count > 0;
                return (
                  <button
                    key={m}
                    type="button"
                      onClick={() => setSelectedMonth((prev) => (prev === i ? null : i))}
                      className={`group relative rounded-xl ${isMobile ? 'px-2 py-2' : 'px-3 py-2.5'} text-center transition-all duration-200 ${
                      isSelected
                        ? `ring-2 ring-indigo-500 shadow-md ${theme === 'dark' ? 'bg-indigo-900/40' : 'bg-indigo-50'} scale-105`
                        : `${colors.surfaceHover} hover:shadow-md hover:scale-105 ${hasData ? 'cursor-pointer' : 'opacity-50 cursor-default'}`
                    }`}
                    disabled={!hasData}
                  >
                    <p className={`${isMobile ? 'text-[10px]' : 'text-xs'} font-medium ${isSelected ? 'text-indigo-700 dark:text-indigo-300' : colors.textMuted} mb-1`}>{m}</p>
                    <p className={`${isMobile ? 'text-sm' : 'text-base'} font-bold ${colors.text}`}>{byMonth[i].count}</p>
                  </button>
                );
              })}
            </div>
          </div>

          </div>
        </div>

        {/* Enhanced Table Section */}
        <div className={`flex-1 min-h-0 flex flex-col ${isMobile ? 'px-2' : 'px-0'}`}>
          <div className={`${colors.surface} ${colors.border} border rounded-xl shadow-md overflow-hidden flex flex-col h-full bg-gradient-to-br ${theme === 'dark' ? 'from-gray-800 to-gray-800' : 'from-white to-gray-50'}`}>
            <div className={`flex-shrink-0 ${isMobile ? 'px-4 py-2.5' : 'px-5 py-3'} border-b ${colors.border} bg-gradient-to-r ${theme === 'dark' ? 'from-gray-800 to-gray-750' : 'from-gray-50 to-white'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className={`text-sm font-semibold ${colors.text} flex items-center gap-2`}>
                    <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <span>Services Ending {monthLabel ? `in ${monthLabel} ${selectedYear}` : `in ${selectedYear}`}</span>
                  </h3>
                  <p className={`text-xs ${colors.textMuted} mt-1 ${isMobile ? 'break-words' : ''}`}>
                    For renewal calls and emails
                  </p>
                </div>
                <div className={`hidden sm:flex items-center gap-3 px-3 py-1.5 rounded-lg ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-white'}`}>
                  <div className="text-right">
                    <p className={`text-[10px] ${colors.textMuted}`}>Total Invoice</p>
                    <p className={`text-xs font-semibold ${colors.text}`}>{formatCurrency(totals.invoice)}</p>
                  </div>
                  <div className="w-px h-6 bg-gray-300 dark:bg-gray-600" />
                  <div className="text-right">
                    <p className={`text-[10px] ${colors.textMuted}`}>Total Cost</p>
                    <p className={`text-xs font-semibold ${colors.text}`}>{formatCurrency(totals.cost)}</p>
                  </div>
                  <div className="w-px h-6 bg-gray-300 dark:bg-gray-600" />
                  <div className="text-right">
                    <p className={`text-[10px] ${colors.textMuted}`}>Total Profit</p>
                    <p className={`text-xs font-semibold ${colors.text}`}>{formatCurrency(totals.profit)}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-hidden">
              <DataTable
                data={tableRows}
                columns={columns}
                loading={isLoading}
                keyExtractor={(row) => row.clientServicetId}
                emptyMessage={
                  monthLabel
                    ? `No services ending in ${monthLabel} ${selectedYear}. Use the list view to manage services.`
                    : `No services with an end date in ${selectedYear}. Use the list view to manage services.`
                }
                showFooter={false}
                sorting={{
                  sortConfig,
                  onSort: handleSort,
                }}
                actions={(item) => getTableActions(item, (id: number) => setDeleteConfirm(id), colors, listContext)}
                actionsPosition="left"
                onRowClick={(item) => {
                  navigate(moduleConfig.module.route.detail(item.clientServicetId));
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Renewal reminders (same component as list view, opens as modal in yearly view) */}
      <ServiceRenewalRemindersPanel
        open={remindersPanelOpen}
        onClose={closeRenewalRemindersPanel}
        serviceItem={selectedServiceForReminders}
        onReminderAdded={mutate}
        variant="modal"
      />

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <ConfirmationDialog
          isOpen={!!deleteConfirm}
          onClose={() => setDeleteConfirm(null)}
          onConfirm={() => handleDelete(deleteConfirm)}
          title={moduleConfig.delete.title}
          message={moduleConfig.delete.message}
          confirmText={moduleConfig.delete.confirmText}
          cancelText={moduleConfig.delete.cancelText}
          confirmVariant="danger"
        />
      )}
    </div>
  );
};

export default ClientServicesYearlyView;
